import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import GeolocationDialog from './GeolocationDialog';
import { useGeolocation } from './hooks/useGeolocation';
import { useGeolocationCheck } from './hooks/useGeolocationCheck';
import { setGeolocationChecked } from './utils';
import type { GeolocationManagerProps } from './types';

/**
 * Главный компонент-менеджер геолокации
 * Проверяет наличие геопозиции при монтировании
 * Управляет состоянием проверки и показывает модальное окно
 * Поддерживает QR-код для мобильных устройств при низкой точности ПК
 */
export const GeolocationManager: React.FC<GeolocationManagerProps> = ({
  username,
  guid,
  children
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Ждем загрузки GUID перед проверкой геолокации
  useEffect(() => {
    // Проверяем наличие GUID в localStorage или пропсах
    const storedGuid = localStorage.getItem('guid');
    if (guid || storedGuid) {
      setIsReady(true);
    } else {
      // Если GUID еще нет, ждем немного и проверяем снова
      const checkInterval = setInterval(() => {
        const currentGuid = localStorage.getItem('guid');
        if (currentGuid) {
          setIsReady(true);
          clearInterval(checkInterval);
        }
      }, 5000); // Проверяем каждые 500мс

      // Таймаут на всякий случай - если через 5 секунд GUID не появился
      setTimeout(() => {
        clearInterval(checkInterval);
        setIsReady(true); // Продолжаем без GUID
      }, 5000);

      return () => clearInterval(checkInterval);
    }
  }, [guid]);

  // Хук проверки наличия геопозиции (запускается только когда isReady)
  const {
    hasLocation,
    isChecking,
    error: checkError
  } = useGeolocationCheck(isReady ? username : '');

  // Хук для запроса геолокации
  const {
    isLoading: isRequestingLocation,
    error: requestError,
    position,
    requestGeolocation
  } = useGeolocation();

  // Показываем/скрываем диалог в зависимости от наличия геопозиции
  useEffect(() => {
    if (!isChecking && !hasLocation) {
      setShowDialog(true);
    } else if (!isChecking && hasLocation) {
      // Если геоданные найдены (в БД или только что получены), скрываем диалог
      setShowDialog(false);
    }
  }, [isChecking, hasLocation]);

  // Обработчик запроса геолокации
  const handleRequestLocation = async () => {
    const success = await requestGeolocation(username, true);

    if (success) {
      // Сохраняем флаг проверки в сессии
      setGeolocationChecked();
      // Закрываем диалог
      setShowDialog(false);
    }
    // Если ошибка или низкая точность - диалог покажет QR-код
  };

  // Обработчик успешного получения координат через QR
  const handleQRSuccess = () => {
    setGeolocationChecked();
    setShowDialog(false);
  };

  // Показываем индикатор загрузки во время ожидания GUID или проверки
  if (!isReady || isChecking) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress size={48} />
        <Box sx={{ color: 'text.secondary' }}>
          {!isReady ? 'Загрузка данных пользователя...' : 'Проверка геолокации...'}
        </Box>
      </Box>
    );
  }

  // Если есть критическая ошибка проверки, показываем ошибку но не блокируем доступ
  if (checkError) {
    console.error('Ошибка проверки геопозиции:', checkError);
    // Не блокируем интерфейс, просто логируем
  }

  return (
    <>
      {/* Модальное окно запроса геопозиции */}
      <GeolocationDialog
        open={showDialog}
        isLoading={isRequestingLocation}
        error={requestError}
        accuracy={position?.accuracy}
        username={username}
        onRequestLocation={handleRequestLocation}
        onQRSuccess={handleQRSuccess}
      />

      {/* Основной контент показывается только если геопозиция получена */}
      {hasLocation || !showDialog ? children : null}
    </>
  );
};

export default GeolocationManager;

